// web-frontend/src/shared/stores/notificationsStore.js
// 🔥🔥🔥 [수정] 로컬 알림 보존 로직 추가 + API 에러 시 방어 코드 + 새 알림 감지 + 스와이프 삭제 기능 + 중복 제거
// 🔥🔥🔥 [핵심 수정] addNotification ID 중복 체크 로직 추가

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import notificationsService from '../services/notificationsService';
import i18nService from '../services/i18n';
import logger from '../utils/logger';

const t = (key, fallback, params = {}) => {
  const value = i18nService.t(key, params);
  return value && value !== key ? value : fallback;
};

const useNotificationsStore = create(
  persist(
    (set, get) => ({
      // ===========================
      // 📊 상태 (State)
      // ===========================
      
      // 기존 상태
      notifications: [], // 🔥 초기값 배열 확인
      unreadCount: 0,
      
      // 🔥 전역 토스트(팝업) 상태
      toast: {
        visible: false,
        message: '',
        type: 'info', // 'success' | 'error' | 'info'
      },

      // 🔕 [2026-04-28] 일반("새 알림 도착") polling 토스트 일시 음소거 시각 (epoch ms).
      // 미팅 등 더 구체적인 글로벌 토스트가 막 표시된 직후, 30초 polling 으로
      // 다시 일반 토스트가 중복 표시되는 것을 방지. partialize 에 포함하지 않아 persist 되지 않음.
      suppressGenericToastUntil: 0,
      
      // 🔥 리마인더 큐 (이벤트 ID, 알림 시간 저장)
      reminders: [], 
      // reminder 구조: { id: 'event-1-1h', eventId: 1, title: '...', triggerAt: ISOString, type: '1hour' | '10min' }
      
      // 기존 설정
      settings: {
        push: true,
        email: true,
        sms: false,
        meetingReminders: true,
        chatMessages: true,
        donationUpdates: true,
        companyFollows: true,
        systemUpdates: true,
        marketing: false,
      },
      
      // 기존 필터
      typeFilter: 'all', // 'all' | 'meeting' | 'chat' | 'donation' | 'system' | 'marketing'
      statusFilter: 'all', // 'all' | 'read' | 'unread'
      
      // 기존 로딩 상태
      isLoading: false,
      isUpdating: false,
      error: null,
      
      // 기존 페이지네이션
      currentPage: 1,
      hasNextPage: false,
      
      // 기존 푸시 토큰
      pushToken: null,
      pushPermissionStatus: 'unknown', // 'unknown' | 'granted' | 'denied' | 'provisional'

      // ===========================
      // 🎬 액션 (Actions)
      // ===========================
      
      // ===========================
      // 🔥 토스트 액션
      // ===========================
      showToast: (message, type = 'info') => {
        set({ toast: { visible: true, message, type } });
        // 3초 후 자동으로 닫기
        setTimeout(() => {
          set((state) => ({ toast: { ...state.toast, visible: false } }));
        }, 3000);
      },

      hideToast: () => {
        set((state) => ({ toast: { ...state.toast, visible: false } }));
      },

      // 🔕 [2026-04-28] 일반 polling 토스트 일시 음소거. 미팅 등 구체 토스트를 띄울 때 호출.
      suppressGenericToast: (ms = 5000) => {
        set({ suppressGenericToastUntil: Date.now() + ms });
      },

      // ===========================
      // 🔥 리마인더 스케줄링 액션
      // ===========================
      addReminder: (reminder) => {
        // reminder 구조: { id: 'event-1-1h', eventId: 1, title: '...', triggerAt: ISOString, type: '1hour' | '10min' }
        set((state) => {
          // 중복 제거 후 추가
          const filtered = state.reminders.filter(r => r.id !== reminder.id);
          return { reminders: [...filtered, reminder] };
        });
      },

      removeRemindersByEventId: (eventId) => {
        set((state) => ({
          reminders: state.reminders.filter(r => r.eventId !== eventId)
        }));
      },

      // 실행된 리마인더 제거
      completeReminder: (reminderId) => {
        set((state) => ({
          reminders: state.reminders.filter(r => r.id !== reminderId)
        }));
      },

      // ===========================
      // 🔥🔥🔥 [핵심 수정] 알림 목록 관리 - 로컬 알림 보존 로직 추가
      // ===========================
      fetchNotifications: async (page = 1, refresh = false) => {
        const isLoadMore = page > 1;
        
        set({ 
          isLoading: !isLoadMore && !refresh,
          error: null 
        });

        try {
          // 🔥 [신규] 이전 unreadCount 저장 (새 알림 감지용)
          const prevUnread = get().unreadCount;
          
          // Service에서 { data, unreadCount, hasNext } 형태로 정제해서 줌
          const response = await notificationsService.getNotifications({
            page,
            limit: 20,
          });
          const newNotifications = response.data;
          
          // 🔥🔥🔥 [핵심 수정] 로컬 알림 보존 로직 추가
          set(state => {
            // 🔥 [수정] 1. 기존 리스트에서 '로컬 알림'만 따로 대피시킵니다.
            // (MainLayout에서 추가한 알림은 id가 'local-'로 시작함)
            const localNotifications = state.notifications.filter(n => 
              typeof n.id === 'string' && n.id.startsWith('local-')
            );

            let updatedList;
            
            if (page === 1) {
              // 🔥 [수정] 2. 첫 페이지 로드 시: [기존 로컬 알림] + [서버 새 데이터] 합치기
              updatedList = [...localNotifications, ...newNotifications];
            } else {
              // 더보기 시: [기존 전체 리스트] + [서버 새 데이터]
              updatedList = [...state.notifications, ...newNotifications];
            }

            // 3. 중복 제거 (ID 기준)
            // Map을 사용하여 ID가 겹치면 덮어씌움 (최신 데이터 우선)
            const uniqueMap = new Map();
            updatedList.forEach(item => uniqueMap.set(item.id, item));
            
            // 4. 리스트로 변환 및 날짜 내림차순 정렬 (최신순)
            const uniqueList = Array.from(uniqueMap.values());
            uniqueList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            // 🔥 [수정] 로컬 알림 중 안 읽은 것이 있으면 unreadCount에 더해줌
            const localUnreadCount = localNotifications.filter(n => !n.isRead && !n.is_read).length;
            // 서버 카운트 + 로컬 카운트
            const totalUnreadCount = (response.unreadCount || 0) + localUnreadCount;

            return {
              notifications: uniqueList,
              unreadCount: totalUnreadCount,
              currentPage: page,
              hasNextPage: response.hasNext,
              isLoading: false,
            };
          });

          // 🔥 [신규] 새 알림이 왔으면 토스트 띄우기 (최초 로드 제외, page 1만)
          // 🔕 [2026-04-28] 직전에 더 구체적인 글로벌 토스트(예: 미팅)가 떴다면 일반 토스트는 음소거.
          if (prevUnread > 0 && prevUnread < response.unreadCount && page === 1) {
            if (Date.now() >= (get().suppressGenericToastUntil || 0)) {
              get().showToast(t('notifications.new_arrived', '🔔 New notification arrived!'), "info");
            }
          }
        } catch (error) {
          logger.error("❌ 알림 로드 실패:", error);
          
          // 🔥🔥🔥 [핵심 수정] 에러 발생 시 빈 배열로 유지하여 UI 충돌 방지
          set({ 
            error: error.message || t('notifications.load_failed', 'Failed to load notifications.'),
            isLoading: false,
            notifications: get().notifications || [] // 🔥 기존 데이터 유지 또는 빈 배열
          }); 
        }
      },

      // 🔥🔥🔥 [수정] 로컬 알림 처리 로직 추가 + is_read와 isRead 둘 다 true로 설정
      markAsRead: async (notificationId) => {
        // 🔥 [신규] 로컬 알림인 경우 API 호출 없이 상태만 변경
        if (typeof notificationId === 'string' && notificationId.startsWith('local-')) {
          set(state => ({
            notifications: state.notifications.map(n => 
              n.id === notificationId ? { ...n, isRead: true, is_read: true } : n
            ),
            unreadCount: Math.max(state.unreadCount - 1, 0)
          }));
          return;
        }

        try {
          await notificationsService.markAsRead(notificationId);
          
          set(state => ({
            notifications: state.notifications.map(notification =>
              notification.id === notificationId
                ? { ...notification, isRead: true, is_read: true } // 🔥 호환성 위해 둘 다 true
                : notification
            ),
            unreadCount: Math.max(state.unreadCount - 1, 0),
          }));
        } catch (error) {
          logger.error("❌ 알림 읽음 처리 실패:", error);
        }
      },

      // 🔥🔥🔥 [수정] is_read와 isRead 둘 다 true로 설정 + 토스트 메시지
      markAllAsRead: async () => {
        set({ isUpdating: true, error: null });
        try {
          await notificationsService.markAllAsRead();
          
          set(state => ({
            notifications: state.notifications.map(notification => ({
              ...notification,
              isRead: true,
              is_read: true, // 🔥 호환성 위해 둘 다 true
              readAt: new Date().toISOString(),
            })),
            unreadCount: 0,
            isUpdating: false,
          }));

          // 🔥 [신규] 성공 토스트 표시
          get().showToast(t('notifications.mark_all_read_success', 'All notifications marked as read.'), "success");
        } catch (error) {
          logger.error("❌ 전체 알림 읽음 처리 실패:", error);
          set({
            error: error.message,
            isUpdating: false,
          });
        }
      },

      // 🔥🔥🔥 [핵심 수정] 로컬 알림 처리 로직 추가 + 낙관적 업데이트 구현 (스와이프용)
      deleteNotification: async (notificationId) => {
        // 🔥 [신규] 로컬 알림이면 API 호출 안 함
        if (typeof notificationId === 'string' && notificationId.startsWith('local-')) {
          set(state => {
            const notification = state.notifications.find(n => n.id === notificationId);
            const wasUnread = notification && (!notification.isRead && !notification.is_read);
            return {
              notifications: state.notifications.filter(n => n.id !== notificationId),
              unreadCount: wasUnread ? Math.max(state.unreadCount - 1, 0) : state.unreadCount
            };
          });
          return;
        }

        try {
          // 🔥 [신규] 1. UI에서 즉시 제거 (낙관적 업데이트)
          const prevNotifications = get().notifications; // 롤백용 백업
          const prevUnreadCount = get().unreadCount;
          
          set(state => {
            const notification = state.notifications.find(n => n.id === notificationId);
            const wasUnread = notification && (!notification.isRead && !notification.is_read);
            
            return {
              notifications: state.notifications.filter(n => n.id !== notificationId),
              unreadCount: wasUnread ? Math.max(state.unreadCount - 1, 0) : state.unreadCount,
            };
          });

          // 🔥 [신규] 2. 서버 요청
          await notificationsService.deleteNotification(notificationId);
          
        } catch (error) {
          logger.error("❌ 알림 삭제 실패:", error);
          
          // 🔥 [신규] 3. 실패 시 재조회로 롤백
          get().fetchNotifications(1);
          
          set({ error: error.message });
          get().showToast(t('notifications.delete_failed', 'Failed to delete notification.'), "error");
        }
      },

      clearAllNotifications: async () => {
        set({ isUpdating: true, error: null });
        try {
          // Service에 clearAll 메서드가 있으면 호출
          await notificationsService.clearAll();
          
          set({
            notifications: [],
            unreadCount: 0,
            isUpdating: false,
          });
        } catch (error) {
          logger.error("❌ 전체 알림 삭제 실패:", error);
          set({
            error: error.message,
            isUpdating: false,
          });
        }
      },

      // ===========================
      // 설정 관리
      // ===========================
      fetchSettings: async () => {
        try {
          const settings = await notificationsService.getSettings();
          set({ settings });
        } catch (error) {
          logger.error('❌ 알림 설정 로드 실패:', error);
        }
      },

      updateSettings: async (newSettings) => {
        set({ isUpdating: true, error: null });
        try {
          const updatedSettings = await notificationsService.updateSettings(newSettings);
          
          set({
            settings: updatedSettings,
            isUpdating: false,
          });
        } catch (error) {
          logger.error("❌ 알림 설정 업데이트 실패:", error);
          set({
            error: error.message,
            isUpdating: false,
          });
          throw error;
        }
      },

      updateSingleSetting: async (key, value) => {
        try {
          const newSettings = { ...get().settings, [key]: value };
          await get().updateSettings(newSettings);
        } catch (error) {
          logger.error("❌ 알림 설정 항목 업데이트 실패:", error);
          set({ error: error.message });
          throw error;
        }
      },

      // ===========================
      // 푸시 알림 관리
      // ===========================
      requestPushPermission: async () => {
        try {
          const permission = await notificationsService.requestPushPermission();
          
          set({
            pushPermissionStatus: permission.status || 'unknown',
            pushToken: permission.token || get().pushToken,
          });
          return permission;
        } catch (error) {
          logger.error("❌ 푸시 권한 요청 실패:", error);
          set({
            error: error.message,
            pushPermissionStatus: 'denied',
          });
          throw error;
        }
      },

      updatePushToken: async (token) => {
        try {
          // await notificationsService.updatePushToken(token);
          set({ pushToken: token });
        } catch (error) {
          logger.error('❌ 푸시 토큰 업데이트 실패:', error);
        }
      },

      // ===========================
      // 실시간 알림 처리
      // ===========================
      // 🔥🔥🔥 [핵심 수정] ID 중복 체크 로직 추가 - UI 중복 표시 방지
      addNotification: (notification) => {
        set(state => {
          // 🔥 [중복 방지] 이미 같은 ID가 존재하면 추가하지 않음
          if (state.notifications.some(n => n.id === notification.id)) {
            logger.warn(`⚠️ [notificationsStore] 중복 알림 감지, 추가 무시: ${notification.id}`);
            return state; // 상태 변경 없이 반환
          }
          
          // 🔥 [수정] 안 읽은 상태면 카운트 증가
          const isUnread = !notification.isRead && !notification.is_read;
          
          return {
            notifications: [notification, ...state.notifications],
            unreadCount: isUnread ? state.unreadCount + 1 : state.unreadCount
          };
        });
      },

      // ===========================
      // 필터링
      // ===========================
      setTypeFilter: (type) => {
        set({ typeFilter: type });
      },

      setStatusFilter: (status) => {
        set({ statusFilter: status });
      },

      getFilteredNotifications: () => {
        const { notifications, typeFilter, statusFilter } = get();
        
        // 🔥 방어 코드: notifications가 배열인지 확인
        if (!Array.isArray(notifications)) {
          logger.warn('⚠️ notifications가 배열이 아닙니다:', notifications);
          return [];
        }
        
        return notifications.filter(notification => {
          const typeMatch = typeFilter === 'all' || notification.type === typeFilter;
          const statusMatch = statusFilter === 'all' || 
            (statusFilter === 'read' && (notification.isRead || notification.is_read)) ||
            (statusFilter === 'unread' && !notification.isRead && !notification.is_read);
          
          return typeMatch && statusMatch;
        });
      },

      // ===========================
      // 페이지네이션
      // ===========================
      loadMore: async () => {
        const { hasNextPage, currentPage, isLoading } = get();
        
        if (hasNextPage && !isLoading) {
          await get().fetchNotifications(currentPage + 1);
        }
      },

      // ===========================
      // 통계 및 유틸리티
      // ===========================
      getStats: () => {
        const { notifications } = get();
        
        // 🔥 방어 코드: notifications가 배열인지 확인
        if (!Array.isArray(notifications)) {
          logger.warn('⚠️ notifications가 배열이 아닙니다:', notifications);
          return {
            total: 0,
            unread: 0,
            byType: {},
            today: 0,
            thisWeek: 0,
          };
        }
        
        const stats = {
          total: notifications.length,
          unread: notifications.filter(n => !n.isRead && !n.is_read).length,
          byType: {},
          today: 0,
          thisWeek: 0,
        };

        const today = new Date().toISOString().split('T')[0];
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        notifications.forEach(notification => {
          // 타입별 통계
          stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
          
          // 날짜별 통계
          const notificationDate = (notification.createdAt || notification.created_at || '').split('T')[0];
          if (notificationDate === today) {
            stats.today++;
          }
          if ((notification.createdAt || notification.created_at) >= weekAgo) {
            stats.thisWeek++;
          }
        });

        return stats;
      },

      // ===========================
      // 스케줄된 알림 관리
      // ===========================
      scheduleReminder: async (reminderData) => {
        try {
          const reminder = await notificationsService.scheduleNotification(
            reminderData.title,
            reminderData.body,
            reminderData.triggerDate,
            reminderData.data
          );
          return reminder;
        } catch (error) {
          logger.error("❌ 리마인더 스케줄링 실패:", error);
          set({ error: error.message });
          throw error;
        }
      },

      cancelScheduledNotification: async (notificationId) => {
        try {
          await notificationsService.cancelScheduledNotification(notificationId);
        } catch (error) {
          logger.error("❌ 스케줄된 알림 취소 실패:", error);
          set({ error: error.message });
          throw error;
        }
      },

      // ===========================
      // 유틸리티 메서드들
      // ===========================
      refresh: async () => {
        await get().fetchNotifications(1, true);
      },

      clearError: () => {
        set({ error: null });
      },

      // 권한 체크
      checkPermissionStatus: async () => {
        try {
          const status = await notificationsService.checkPermissionStatus();
          set({ pushPermissionStatus: status.status || 'unknown' });
          return status.status || 'unknown';
        } catch (error) {
          logger.error('❌ 권한 상태 확인 실패:', error);
          return 'unknown';
        }
      },

      // 즉시 알림 표시
      showLocalNotification: (notification) => {
        // 로컬 알림을 즉시 표시하는 로직
        notificationsService.sendLocalNotification(
          notification.title,
          notification.body,
          notification.data
        );
        
        // 스토어에도 추가
        get().addNotification({
          ...notification,
          id: `local-${Date.now()}`,
          isLocal: true,
          createdAt: new Date().toISOString(),
          created_at: new Date().toISOString(),
          isRead: false,
          is_read: false,
        });
      },

      // 배지 수 업데이트
      updateBadgeCount: () => {
        const { unreadCount } = get();
        notificationsService.setBadgeCount(unreadCount);
      },
    }),
    {
      name: 'notifications-storage',
      // 🔥 [핵심 수정] React Native의 AsyncStorage 대신 Web의 localStorage 사용
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        settings: state.settings,
        reminders: state.reminders, // 🔥 리마인더 영구 저장
        pushToken: state.pushToken,
        pushPermissionStatus: state.pushPermissionStatus,
        typeFilter: state.typeFilter,
        statusFilter: state.statusFilter,
        // 🔥 알림 데이터는 캐싱하지 않거나, 필요하면 포함 (현재는 제외)
      }),
    }
  )
);

// ✅ named export 추가
export default useNotificationsStore;
export { useNotificationsStore };

// CommonJS 호환성
if (typeof module !== 'undefined' && module.exports) {
  module.exports = useNotificationsStore;
  module.exports.default = useNotificationsStore;
  module.exports.useNotificationsStore = useNotificationsStore;
}
